import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  DatePicker,
  Table,
  Collapse,
  message,
  Typography,
  Select,
} from "antd";
import axios from "axios";
import type { ColumnsType } from "antd/es/table";
import {
  shiftClosureSubmit,
  getShiftClosureSummary,
  shiftPendingPatientDetails,
} from "../../apis/nephroPlusService/OHDRService";
import dayjs from "dayjs";
import { useMediaQuery } from "react-responsive";
import { Divider } from "antd";
import { List } from "antd";

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface SummaryData {
  [key: string]: number;
}

interface AppointmentVisit {
  scheduledDate?: string;
  scheduledTime?: string;
  status?: string;
  billStatus?: string;
}

interface PendingPatient {
  patientName: string;
  caseId: string;
  visitId: string;
  appointmentVisit?: AppointmentVisit;
}

interface PendingData {
  pendingCheckedInPatients?: PendingPatient[];
  pendingCheckedOutPatients?: PendingPatient[];
  pendingBillPatients?: PendingPatient[];
}

interface ShiftClosureModalProps {
  visible: boolean;
  onCancel: () => void;
  hospitalId: any;
  schemeId: any;
}

// Table columns
const summaryColumns: ColumnsType<{ key: string; value: number }> = [
  {
    title: "Metric",
    dataIndex: "key",
    key: "key",
  },
  {
    title: "Count",
    dataIndex: "value",
    key: "value",
  },
];

const pendingPatientColumns: ColumnsType<any> = [
  {
    title: "Patient Name",
    dataIndex: "patientName",
    key: "patientName",
  },
  {
    title: "Case ID",
    dataIndex: "caseId",
    key: "caseId",
  },
  {
    title: "Visit ID",
    dataIndex: "visitId",
    key: "visitId",
  },
  {
    title: "Date",
    dataIndex: "scheduledDate",
    key: "scheduledDate",
  },
  {
    title: "Time",
    dataIndex: "scheduledTime",
    key: "scheduledTime",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
  },
  {
    title: "Bill Status",
    dataIndex: "billStatus",
    key: "billStatus",
  },
];

const ShiftClosureModal: React.FC<ShiftClosureModalProps> = ({
  visible,
  onCancel,
  hospitalId,
  schemeId,
}) => {
  const formatKeyToTitle = (key: string): string => {
    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2") // add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 });

  const [selectedDate, setSelectedDate] = useState<any>(dayjs());
  const [summaryColumns, setSummaryColumns] = useState<ColumnsType<any>>([]);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [pendingData, setPendingData] = useState<PendingData>({});
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingShiftClosure, setLoadingShiftClosure] = useState(false);
  const [loadingPending, setLoadingPending] = useState(false);
  const [dialysisSummaryData, setDialysisSummaryData] = useState<any>();
  const [shift, setShift] = useState<string | null>("MORNING");

  useEffect(() => {
    if (visible) {
      setShift("MORNING");
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      fetchPendingData();
      //   fetchDropdownCounts();
      handleFetchSummary();
    }
  }, [visible, selectedDate, shift]);

  useEffect(() => {
    handleFetchSummary();
  }, []);

  const handleFetchSummary = async () => {
    if (!selectedDate) {
      message.warning("Please select a date");
      return;
    }

    setLoadingSummary(true);
    try {
      const response: any = await getShiftClosureSummary(
        dayjs(selectedDate).format("YYYY-MM-DD"),
        shift,
        hospitalId,
        schemeId
      );
      const dynamicColumns: ColumnsType<any> = Object.keys(response?.data)?.map(
        (key) => ({
          title: formatKeyToTitle(key),
          dataIndex: key,
          key,
        })
      );

      setSummaryColumns(dynamicColumns);
      setSummaryData([response?.data]);
    } catch (error) {
      message.error("Failed to fetch summary data");
      setSummaryData([]);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleShiftClosure = async () => {
    if (!selectedDate) {
      message.warning("Please select a date");
      return;
    }

    const {
      totalCheckedIn,
      totalCheckedOut,
      totalOTCFilled,
      totalOHDRFilled,
      totalBillUploaded,
    } = summaryData?.[0];

    const isValid =
      totalCheckedIn === totalCheckedOut &&
      totalCheckedIn === totalOTCFilled &&
      totalCheckedIn === totalOHDRFilled &&
      totalCheckedIn === totalBillUploaded;

    if (!isValid) {
      message.warning(
        "There are outstanding items. Please clear them before shift closure."
      );
      return;
    }

    setLoadingShiftClosure(true);
    try {
      await shiftClosureSubmit(
        dayjs(selectedDate).format("YYYY-MM-DD"),
        shift,
        hospitalId,
        schemeId
      );
      message.success("Shift closed successfully");
    } catch (error) {
      message.error("Shift closure failed");
    } finally {
      setLoadingShiftClosure(false);
    }
  };

  const fetchPendingData = async () => {
    setLoadingPending(true);
    try {
      if (!selectedDate) {
        message.warning("Please select a date");
        return;
      }

      if (selectedDate) {
        const response = await shiftPendingPatientDetails(
          dayjs(selectedDate).format("YYYY-MM-DD"),
          shift,
          hospitalId,
          schemeId
        );
        setPendingData(response?.data || {});
      }
    } catch (error) {
      message.error("Failed to fetch pending data");
      setPendingData({});
    } finally {
      setLoadingPending(false);
    }
  };

  //   const fetchDropdownCounts = async () => {
  //     try {
  //       if (!selectedDate) {
  //         message.warning("Please select a date");
  //         return;
  //       }

  //       if (selectedDate) {
  //         const response = await getDropdownCounts(
  //           dayjs(selectedDate).format("YYYY-MM-DD")
  //         );
  //         setDialysisSummaryData(response?.data || {});
  //       }
  //     } catch (error) {
  //       message.error("Failed to fetch dropdown counts");
  //       setDialysisSummaryData({});
  //     }
  //   };

  const getPendingDataSource = (list: PendingPatient[] = []) =>
    list.map((item) => ({
      key: item.visitId,
      patientName: item.patientName,
      caseId: item.caseId,
      visitId: item.visitId,
      scheduledDate: item.appointmentVisit?.scheduledDate || "",
      scheduledTime: item.appointmentVisit?.scheduledTime || "",
      status: item.appointmentVisit?.status || "",
      billStatus: item.appointmentVisit?.billStatus || "",
    }));

  const pendingSections = [
    { key: "pendingCheckedInPatients", label: "Pending Checked-In Patients" },
    { key: "pendingCheckedOutPatients", label: "Pending Checked-Out Patients" },
    { key: "pendingBillPatients", label: "Pending Bill Patients" },
  ] as const;

  const formatTitle = (key: string) =>
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());

  const getMaxRowCount = (
    data: Record<string, { value: string; count: number }[]>
  ) => {
    return Math.max(...Object.values(data).map((arr) => arr.length));
  };

  const DialysisSummaryStructuredTable = ({
    data,
  }: {
    data: Record<string, { value: string; count: number }[]>;
  }) => {
    if (!data) return null;

    const maxRows = getMaxRowCount(data);
    const columns = Object.entries(data).flatMap(([key]) => [
      {
        title: key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (s) => s.toUpperCase()),
        children: [
          {
            title: "Name",
            dataIndex: `${key}_name`,
            key: `${key}_name`,
            align: "center",
          },
          {
            title: "Count",
            dataIndex: `${key}_count`,
            key: `${key}_count`,
            align: "center",
          },
        ],
      },
    ]);

    // Build rows
    const dataSource = Array.from({ length: maxRows }).map((_, rowIndex) => {
      const row: Record<string, any> = { key: rowIndex };
      for (const [key, values] of Object.entries(data)) {
        row[`${key}_name`] = values[rowIndex]?.value || "";
        row[`${key}_count`] = values[rowIndex]?.count ?? "";
      }
      return row;
    });

    return (
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
        size="small"
        scroll={{ x: "max-content" }}
        className="spaced-table-day-closure"
        rowClassName={(_, index) => (index % 2 === 0 ? "ohdr-row-alt" : "")}
      />
    );
  };

  // const renderDialysisSummaryTables = (data: any) => {
  //   return Object.entries(data).map(([key, items]: [any, any]) => {
  //     const columns = [
  //       {
  //         title: "Name",
  //         dataIndex: "value",
  //         key: "value",
  //       },
  //       {
  //         title: "Count",
  //         dataIndex: "count",
  //         key: "count",
  //       },
  //     ];

  //     return (
  //       <div key={key} style={{ marginBottom: 12 }}>
  //         <Title level={5}>{formatTitle(key)}</Title>
  //         <Table
  //           columns={columns}
  //           dataSource={items?.map((item: any, index: any) => ({
  //             ...item,
  //             key: index,
  //           }))}
  //           className="spaced-table-day-closure"
  //           rowClassName={(_, index) => (index % 2 === 0 ? "ohdr-row-alt" : "")}
  //           pagination={false}
  //           size="small"
  //           bordered
  //         />
  //         <Divider />
  //       </div>
  //     );
  //   });
  // };

  return (
    <Modal
      visible={visible}
      title="Shift Summary"
      onCancel={onCancel}
      footer={<Button onClick={onCancel}>Close</Button>}
      // width={6000}
      width={isMobile ? "98vw" : isTablet ? "98vw" : 1200}
      wrapClassName={
        isMobile ? "day-closure-modal-responsive" : "day-closure-modal"
      }
      bodyStyle={{
        maxHeight: isMobile ? "90vh" : isTablet ? "90vh" : "135vh",
        overflowY: "auto",
        padding: isMobile ? 4 : isTablet ? 12 : 24,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: isMobile ? 8 : 12,
          marginBottom: 16,
          flexWrap: "wrap",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <DatePicker
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          format={"YYYY-MM-DD"}
          style={{ width: isMobile ? "100%" : undefined }}
        />
        <Select
          placeholder="Select Shift"
          value={shift}
          onChange={setShift}
          style={{ width: isMobile ? "100%" : 150 }}
        >
          <Select.Option value="MORNING">Morning</Select.Option>
          <Select.Option value="AFTERNOON">Afternoon</Select.Option>
          <Select.Option value="EVENING">Evening</Select.Option>
        </Select>
        <Button
          type="primary"
          onClick={handleFetchSummary}
          loading={loadingSummary}
          style={{
            backgroundColor: "rgb(0, 76, 143)",
            color: "#fff",
            boxShadow: "0px 2px 8px 0px #00000040",
            width: `${isMobile ? "100%" : "10%"}`,
          }}
        >
          Fetch Details
        </Button>
      </div>

      {(summaryData || dialysisSummaryData) && (
        <>
          <Table
            dataSource={summaryData}
            columns={summaryColumns}
            pagination={false}
            className="spaced-table-day-closure"
            rowClassName={(_, index) => (index % 2 === 0 ? "ohdr-row-alt" : "")}
            bordered
            size="small"
            rowKey="key"
            scroll={isMobile ? { x: "max-content" } : undefined}
          />
          {/* <div
            style={{ marginTop: "8px", maxHeight: "188px", overflowY: "auto" }}
          >
            <h3>Consumable Items</h3>
            {dialysisSummaryData && (
              <DialysisSummaryStructuredTable data={dialysisSummaryData} />
            )}
          </div> */}

          <div
            style={{ textAlign: isMobile ? "center" : "right", marginTop: 16 }}
          >
            <Button
              type="primary"
              onClick={handleShiftClosure}
              loading={loadingShiftClosure}
              style={{
                backgroundColor: "rgb(0, 76, 143)",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: isMobile ? "100%" : "10%",
              }}
            >
              Shift Closure
            </Button>
          </div>
        </>
      )}

      <Collapse
        defaultActiveKey={["pendingCheckedInPatients"]}
        style={{ marginTop: 16 }}
        className="ohdr-collapse padding-correction"
        accordion
        expandIconPosition="end"
      >
        {pendingSections?.map(({ key, label }) => (
          <Panel
            header={
              <div
                style={{
                  background: "rgb(0, 76, 143)",
                  color: "#fff",
                  padding: "4px 12px",
                  borderRadius: "4px",
                }}
              >
                {label}
              </div>
            }
            key={key}
          >
            <div
              style={{
                maxHeight: isMobile ? "120px" : "244px",
                overflowY: "auto",
              }}
            >
              <Table
                dataSource={getPendingDataSource(pendingData[key])}
                columns={pendingPatientColumns}
                pagination={false}
                className="spaced-table-day-closure"
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "ohdr-row-alt" : ""
                }
                size="small"
                loading={loadingPending}
                rowKey="visitId"
                scroll={isMobile ? { x: "max-content" } : undefined}
              />
            </div>
          </Panel>
        ))}
      </Collapse>
    </Modal>
  );
};

export default ShiftClosureModal;
